import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ReactGa from 'react-ga4';

import { AnimationShell, Breadcrumb, Button, Icon, Link, Typography } from '@/components';
import { Route } from '@/constants';
import { useHeader } from '@/features/header';
import { cn } from '@/utils/styles';

import { AvatarDropdown, AvatarDropdownItem } from '../avatar-dropdown';
// import { useCompanyRole } from '@/features/company/hooks';
import { CreateOpportunityDrawer } from '@/features/opportunities';
import { InviteMemberDrawer } from '@/features/users/components/team/invite-member-drawer';
import { SidebarTrigger } from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const navigate = useNavigate();

  const { t } = useTranslation(['navbar']);

  const { breadcrumb } = useHeader();
  // const { isFree } = useCompanyRole();

  const dropdownItems = useMemo(
    () =>
      [
        {
          label: t('Profile settings'),
          icon: 'person',
          onClick: () => navigate(Route.ProfileSettings),
        },
        {
          label: t('Plans & Billing'),
          icon: 'credit-card',
          onClick: () => navigate(Route.PlanSettings),
        },
        {
          label: t('Company'),
          icon: 'buildings',
          onClick: () => navigate(Route.CompanySettings),
        },
        // {
        //   label: t('Team'),
        //   icon: 'people',
        //   onClick: () => navigate(Route.TeamSettings),
        // },
      ] as AvatarDropdownItem[],
    [t, navigate]
  );

  const closeInvite = useCallback(() => setIsInviteOpen(false), []);

  const onClose = useCallback(() => {
    setIsOpen(false);

    ReactGa.event({
      category: 'form_closed',
      action: 'Opportunity creation proccess cancelled',
      label: 'User has abandoned the opportunity creation process',
    });
  }, []);

  return (
    <>
      <header
        className={cn(
          'z-10 h-16 w-full px-8',
          'flex shrink-0 items-center justify-between',
          'border-b border-neutral-50 bg-white'
        )}
      >
        <div className="flex items-center">
          <SidebarTrigger className="hover:cursor-pointer" />
          <Breadcrumb items={breadcrumb} />
        </div>
        <AnimationShell type="scale" className="hidden items-center justify-center gap-4 lg:flex">
          <Button size="md" color="primary" onClick={() => setIsInviteOpen(true)} icon="user-plus">
            {t('Invite your team')}
          </Button>
          <Button size="md" color="ghostPrimary" icon="plus" onClick={() => setIsOpen(true)}>
            {t('Create project')}
          </Button>
          {/* <AvatarDropdown options={dropdownItems} /> */}
        </AnimationShell>

        {/* Mobile + Tablet (portrait and landscape): dropdown */}
        <div className="lg:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger className="group outline-none">
                <span className="sr-only">Open menu</span>
                <Icon name="more-horizontal" className="text-primary-900 h-6 w-6" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => setIsInviteOpen(true)}>
                <Icon name="user-plus" className="text-primary-900 h-6 w-6" />
                <Typography as="p" size="body-2" color="primary" weight="normal">
                  {t('Invite your team')}
                </Typography>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setIsOpen(true)}>
                <Icon name="plus" className="text-primary-900 h-6 w-6" />
                <Typography as="p" size="body-2" color="primary" weight="normal">
                  {t('Create project')}
                </Typography>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <CreateOpportunityDrawer isOpen={isOpen} onClose={onClose} />
      <InviteMemberDrawer isOpen={isInviteOpen} onClose={closeInvite} />
    </>
  );
}
